import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  refreshAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const isDevelopment = process.env.NODE_ENV === 'development';

// Axios default config - HEM LOCAL HEM PRODUCTION İÇİN AYNI!
// React proxy kullanacak, relative path kullanıyoruz
axios.defaults.baseURL = '/api';

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

// Axios request interceptor - Ticimax isteklerini DETAYLI logla
axios.interceptors.request.use(
  (config) => {
    if (isDevelopment && config.url?.includes('ticimax')) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🚀 AXIOS REQUEST YAKALANDI!');
      console.log('   Method:', config.method?.toUpperCase());
      console.log('   URL:', config.url);
      console.log('   Params:', config.params);
      console.log('   Data:', config.data);
      
      if (config.method === 'post' && config.url?.includes('update-manual-content')) {
        console.log('');
        console.log('⚠️  ⚠️  ⚠️  TİCİMAX\'A VERI GÖNDERİLİYOR! ⚠️  ⚠️  ⚠️');
        console.log('   UrunKartId:', config.data?.urunKartId);
        console.log('   Önyazı uzunluğu:', config.data?.manuelOnYazi?.length);
        console.log('   Açıklama uzunluğu:', config.data?.manuelAciklama?.length);
        console.log('   📦 Request Body kullanılıyor (URL params değil - 431 hatasını önler)');
        console.log('');
        console.trace('Stack trace:'); // Nereden çağrıldığını göster
      }
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Axios response interceptor - Token yenileme ile
// Bu interceptor AuthProvider dışında tanımlanmalı (global)
// Ama şimdilik burada bırakıyoruz, AuthProvider içinde doğru şekilde kuracağız

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileFetched, setProfileFetched] = useState(false);

  // Refresh token fonksiyonu
  const refreshAccessTokenFunc = async (): Promise<string | null> => {
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');
      
      if (!storedRefreshToken) {
        console.warn('⚠️ Refresh token bulunamadı');
        return null;
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Access token yenileniyor...');
      }
      
      // Refresh endpoint'ine istek at
      const response = await axios.post('/auth/refresh', {
        refresh_token: storedRefreshToken
      }, {
        // Bu istek için Authorization header'ı kullanma
        headers: {
          Authorization: undefined
        }
      });
      
      const { access_token, refresh_token, user: userData } = response.data;
      
      // Yeni token'ları kaydet
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setRefreshToken(refresh_token);
      setUser(userData);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Token yenilendi başarıyla');
      }
      
      return access_token;
    } catch (error: any) {
      console.error('❌ Token yenileme hatası:', error);
      
      // Refresh token da geçersizse logout yap
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setRefreshToken(null);
      setUser(null);
      
      toast.error('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
      
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      if (process.env.NODE_ENV === 'development') console.log('🚀 AuthContext initialized');
      const storedToken = localStorage.getItem('authToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔑 Stored access token:', storedToken ? 'MEVCUT' : 'YOK');
        console.log('🔑 Stored refresh token:', storedRefreshToken ? 'MEVCUT' : 'YOK');
      }
      
      if (storedToken && storedRefreshToken) {
        // Token varsa header'a ekle
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        setToken(storedToken);
        setRefreshToken(storedRefreshToken);
        
        // Sadece user yoksa profile fetch et
        if (!user && !profileFetched) {
          if (process.env.NODE_ENV === 'development') console.log('📡 Profile fetch ediliyor...');
          await fetchProfile();
          setProfileFetched(true);
        } else {
          if (process.env.NODE_ENV === 'development') console.log('✅ User zaten mevcut, fetch atlandı');
          setLoading(false);
        }
      } else {
        if (process.env.NODE_ENV === 'development') console.log('⚠️ Token yok, loading false');
        setLoading(false);
      }
    };
    
    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Sadece mount'ta çalış

  // Axios response interceptor - 401 hatalarında otomatik token yenileme
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => {
        if (isDevelopment && response.config.url?.includes('ticimax')) {
          console.log('✅ AXIOS RESPONSE:', response.config.url, '- Status:', response.status);
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        
        // 401 hatası ve henüz retry yapılmamışsa
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Refresh endpoint'ine yapılan isteği tekrar deneme
          if (originalRequest.url?.includes('/auth/refresh')) {
            return Promise.reject(error);
          }
          
          if (isRefreshing) {
            // Başka bir istek zaten refresh yapıyorsa, bekle
            return new Promise((resolve) => {
              subscribeTokenRefresh((newToken: string) => {
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                resolve(axios(originalRequest));
              });
            });
          }
          
          originalRequest._retry = true;
          isRefreshing = true;
          
          try {
            const newToken = await refreshAccessTokenFunc();
            
            if (newToken) {
              isRefreshing = false;
              onRefreshed(newToken);
              
              // Orijinal isteği yeni token ile tekrar dene
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            isRefreshing = false;
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    // Cleanup
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const fetchProfile = async () => {
    if (process.env.NODE_ENV === 'development') console.log('🔍 fetchProfile çağrıldı');
    try {
      if (process.env.NODE_ENV === 'development') console.log('📤 Profile API isteği gönderiliyor...');
      const response = await axios.get('/auth/profile');
      if (process.env.NODE_ENV === 'development') console.log('✅ Profile başarıyla alındı:', response.data);
      setUser(response.data);
      setLoading(false);
    } catch (error: any) {
      console.error('❌ Profile fetch error:', error);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error data:', error.response?.data);
      
      // 401 Unauthorized ise - interceptor zaten token yenilemeyi deneyecek
      // Eğer interceptor başarısız olduysa zaten logout edilmiş olacak
      if (error.response?.status === 401) {
        if (process.env.NODE_ENV === 'development') console.log('🔴 Token geçersiz (401)');
        // Interceptor handle edecek, burada sadece loading'i false yap
      } else {
        // Diğer hatalar için token'ı koru, sadece log yap
        console.warn('⚠️ Profile fetch hatası ama token korunuyor:', error.message);
      }
      
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    if (process.env.NODE_ENV === 'development') console.log('🔐 Login başlatıldı:', email);
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { access_token, refresh_token, user } = response.data;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Login response alındı:', { 
          access_token: access_token ? 'MEVCUT' : 'YOK',
          refresh_token: refresh_token ? 'MEVCUT' : 'YOK',
          user 
        });
      }
      
      // Token'ları kaydet
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('💾 Tokens saved to localStorage');
      }
      
      // User bilgisini direkt set et (fetchProfile'a gerek yok)
      setToken(access_token);
      setRefreshToken(refresh_token);
      setUser(user);
      setProfileFetched(true); // Profile'ı tekrar fetch etme
      setLoading(false);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ User state set edildi:', user);
      }
      
      toast.success('Giriş başarılı!');
      
      // User'ı return et (Login.tsx'te role-based routing için)
      return user;
    } catch (error: any) {
      console.error('❌ Login hatası:', error);
      setLoading(false);
      toast.error(error.response?.data?.message || 'Giriş başarısız!');
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    toast.success('Çıkış yapıldı');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      refreshToken,
      login, 
      logout, 
      loading,
      refreshAccessToken: refreshAccessTokenFunc
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};







